// frontend/src/hooks/useRelevantStops.js
// Custom hook for filtering relevant stops and calculating dynamic distances

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseNullableNonNegativeMetric, sumRequiredMetrics } from '../utils/nullableMetric';

const getWaypointOrderValue = (waypoint = {}) => {
    const effectiveSequence = Number(waypoint.effective_sequence);
    if (Number.isFinite(effectiveSequence)) {
        return effectiveSequence;
    }

    const sequenceNumber = Number(waypoint.sequence_number);
    return Number.isFinite(sequenceNumber) ? sequenceNumber : Number.MAX_SAFE_INTEGER;
};

const sortWaypointsByDisplayOrder = (waypoints = []) => (
    [...waypoints].sort((left, right) => {
        const orderDiff = getWaypointOrderValue(left) - getWaypointOrderValue(right);
        if (orderDiff !== 0) {
            return orderDiff;
        }

        return (Number(left.sequence_number) || 0) - (Number(right.sequence_number) || 0);
    })
);

const isRouteDetailWaypoint = (waypoint = {}) => waypoint.point_type === 'waypoint';

const isDisplayStopWaypoint = (waypoint = {}) => (
    waypoint.waypoint_id !== 'current_location'
    && waypoint.point_type !== 'origin'
    && !isRouteDetailWaypoint(waypoint)
);

const isStartLikeWaypoint = (waypoint = {}) => (
    Number(waypoint.sequence_number) === 0
    || Number(waypoint.effective_sequence) === 0
    || waypoint.is_departure === true
    || waypoint.point_type === 'start'
    || waypoint.stop_position === 'start'
);

const getWaypointKey = (waypointId) => String(waypointId);

const getCompletedEstimatedWaypoints = (estimatedData) => {
    if (Array.isArray(estimatedData)) {
        return estimatedData;
    }

    if (Array.isArray(estimatedData?.waypoints)) {
        return estimatedData.waypoints;
    }

    return [];
};

const isCompletedDisplaySkippedStop = (stops, index) => (
    Boolean(stops[index]?.is_skipped)
    && index !== 0
    && index !== stops.length - 1
);

const findPreviousCompletedAnchorStop = (stops, currentIndex) => {
    for (let index = currentIndex - 1; index >= 0; index -= 1) {
        if (!isCompletedDisplaySkippedStop(stops, index)) {
            return stops[index];
        }
    }

    return null;
};

const getWaypointsInCompletedSegment = (waypoints, previousStop, currentStop) => {
    if (!previousStop || !currentStop) {
        return [];
    }

    const previousSequence = getWaypointOrderValue(previousStop);
    const currentSequence = getWaypointOrderValue(currentStop);

    return waypoints.filter((waypoint) => {
        const sequence = getWaypointOrderValue(waypoint);
        return sequence > previousSequence && sequence <= currentSequence;
    });
};

const findPreviousWaypointInOrderedList = (waypoints, currentWaypoint) => {
    if (!currentWaypoint) {
        return null;
    }

    const currentKey = getWaypointKey(currentWaypoint.waypoint_id);
    const currentIndex = waypoints.findIndex(
        (waypoint) => getWaypointKey(waypoint.waypoint_id) === currentKey
    );
    if (currentIndex <= 0) {
        return null;
    }

    return waypoints[currentIndex - 1] || null;
};

// A manual skip remains visible in completed summaries, but it is deliberately
// not an arrival anchor. The next real stop can still trust its actual segment
// distance only when every missing intermediate row is skipped history.
const hasOnlySkippedCompletedIntermediates = (displaySegmentWaypoints, currentWaypoint) => {
    const currentKey = getWaypointKey(currentWaypoint?.waypoint_id);
    const currentIndex = displaySegmentWaypoints.findIndex(
        (waypoint) => getWaypointKey(waypoint.waypoint_id) === currentKey
    );
    if (currentIndex < 0) {
        return false;
    }

    return displaySegmentWaypoints
        .slice(0, currentIndex)
        .every((waypoint) => Boolean(waypoint?.is_skipped));
};

const canUseEstimatedCompletedSegmentFallback = (actualSegmentWaypoints, estimatedSegmentWaypoints) => {
    if (!actualSegmentWaypoints.length || actualSegmentWaypoints.length !== estimatedSegmentWaypoints.length) {
        return false;
    }

    return actualSegmentWaypoints.every((waypoint, index) => {
        const estimatedWaypoint = estimatedSegmentWaypoints[index];
        if (!estimatedWaypoint) {
            return false;
        }

        return getWaypointKey(estimatedWaypoint.waypoint_id) === getWaypointKey(waypoint.waypoint_id)
            && Boolean(estimatedWaypoint.is_skipped) === Boolean(waypoint.is_skipped);
    });
};

const doesCompletedSegmentTopologyMatch = (leftSegmentWaypoints, rightSegmentWaypoints) => {
    if (!leftSegmentWaypoints.length || leftSegmentWaypoints.length !== rightSegmentWaypoints.length) {
        return false;
    }

    return leftSegmentWaypoints.every((waypoint, index) => {
        const counterpartWaypoint = rightSegmentWaypoints[index];
        if (!counterpartWaypoint) {
            return false;
        }

        return getWaypointKey(counterpartWaypoint.waypoint_id) === getWaypointKey(waypoint.waypoint_id)
            && Boolean(counterpartWaypoint.is_skipped) === Boolean(waypoint.is_skipped);
    });
};

const parseDateOrNull = (value) => {
    if (!value) {
        return null;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getCompletedActualDepartureTime = (displayStop = {}, actualWaypoint = {}) => {
    const arrivalTime = parseDateOrNull(actualWaypoint?.actual_arrival_time);
    if (!arrivalTime) {
        return null;
    }

    const stayDurationSeconds = Number(actualWaypoint?.stay_duration_seconds);
    if (Number.isFinite(stayDurationSeconds) && stayDurationSeconds >= 0) {
        return new Date(arrivalTime.getTime() + (stayDurationSeconds * 1000));
    }

    const stayDurationMinutes = Number(displayStop?.stay_duration_minutes);
    if (Number.isFinite(stayDurationMinutes) && stayDurationMinutes >= 0) {
        return new Date(arrivalTime.getTime() + (stayDurationMinutes * 60 * 1000));
    }

    return arrivalTime;
};

const isCompletedAnchorMateriallyShorterThanEstimate = (actualDistance, estimatedDistance) => (
    Number.isFinite(actualDistance)
    && actualDistance >= 0
    && Number.isFinite(estimatedDistance)
    && estimatedDistance > 0
    && actualDistance <= 250
    && actualDistance <= estimatedDistance * 0.2
);

const buildPoisonedCompletedAnchorKeySet = (stops, actualWaypointMap, estimatedWaypointMap) => {
    const poisonedKeys = new Set();

    for (let index = 1; index < stops.length; index += 1) {
        if (isCompletedDisplaySkippedStop(stops, index)) {
            continue;
        }

        const stop = stops[index];
        const stopKey = getWaypointKey(stop?.waypoint_id);
        const currentActualStop = actualWaypointMap.get(stopKey);
        const currentArrivalTime = parseDateOrNull(currentActualStop?.actual_arrival_time);
        if (!currentArrivalTime) {
            continue;
        }

        const previousVisibleStop = findPreviousCompletedAnchorStop(stops, index);
        if (!previousVisibleStop) {
            continue;
        }

        const previousActualStop = actualWaypointMap.get(getWaypointKey(previousVisibleStop.waypoint_id));
        const previousDepartureTime = getCompletedActualDepartureTime(previousVisibleStop, previousActualStop);
        if (!previousDepartureTime || currentArrivalTime.getTime() !== previousDepartureTime.getTime()) {
            continue;
        }

        const estimatedStop = estimatedWaypointMap.get(stopKey);
        const actualDistance = Number(currentActualStop?.total_distance_meters);
        const estimatedDistance = Number(estimatedStop?.total_distance_meters);
        if (!isCompletedAnchorMateriallyShorterThanEstimate(actualDistance, estimatedDistance)) {
            continue;
        }

        poisonedKeys.add(stopKey);
    }

    return poisonedKeys;
};

const normalizeNonNegativeNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.max(0, numericValue) : null;
};

const sumRequiredStaticDistanceMeters = (waypoints = []) => {
    let totalDistanceMeters = 0;
    for (const waypoint of waypoints) {
        const rawDistanceMeters = waypoint?.total_distance_meters;
        const distanceMeters = Number(rawDistanceMeters);
        if (
            rawDistanceMeters === null
            || rawDistanceMeters === undefined
            || rawDistanceMeters === ''
            || !Number.isFinite(distanceMeters)
            || distanceMeters < 0
        ) {
            return null;
        }
        totalDistanceMeters += distanceMeters;
    }
    return totalDistanceMeters;
};

/**
 * Filter and calculate dynamic distances for relevant stops
 */
export const useRelevantStops = (
    displayData,
    isDriver,
    pickupStopId,
    dropoffStopId,
    tripData,
    showWaypoints,
    etaData
) => {
    const { t } = useTranslation();

    return useMemo(() => {
        if (!displayData?.waypoints || !Array.isArray(displayData.waypoints)) {
            console.log(t('noValidWaypointsData'));
            return [];
        }

        const orderedDisplayWaypoints = sortWaypointsByDisplayOrder(displayData.waypoints.filter(
            wp => wp.waypoint_id !== 'current_location' && wp.point_type !== 'origin'
        ));

        const filteredWaypoints = orderedDisplayWaypoints.filter((stop) => {
            if (isDriver) {
                return showWaypoints ? true : !isRouteDetailWaypoint(stop);
            }

            if (!isDisplayStopWaypoint(stop)) {
                return false;
            }

            const isUserPickupOrDropoff = stop.waypoint_id === pickupStopId || stop.waypoint_id === dropoffStopId;
            if (isUserPickupOrDropoff) {
                return true;
            }

            if (
                isStartLikeWaypoint(stop)
                && displayData?.type === 'websocket_realtime'
                && stop.distance_meters_from_current !== undefined
            ) {
                return stop.distance_meters_from_current < 100;
            }

            return true;
        });

        console.log(`[ETADisplay] Filtering check - Status: ${tripData?.status}, DisplayType: ${displayData?.type}, IsLive: ${displayData?.isLive}`);
        console.log(`[ETADisplay] Waypoints before filtering: ${displayData.waypoints?.length || 0}`);

        console.log(`[ETADisplay] FILTERING COMPLETE:`);
        console.log(`[ETADisplay] Original: ${displayData.waypoints.length}, Filtered: ${filteredWaypoints.length}`);
        console.log(`[ETADisplay] Remaining waypoints:`, filteredWaypoints.map(w => ({ name: w.waypoint_name, is_passed: w.is_passed, sequence: w.sequence_number })));

        const allStops = filteredWaypoints;
        if (displayData.type === 'active_unavailable') {
            // Filtering still owns rider/detail visibility. Metric aggregation
            // must not turn unknown active travel into static legs or zeros.
            return allStops.map(stop => ({
                ...stop,
                dynamic_distance_meters: null,
                dynamic_duration_minutes: null,
                gross_dynamic_distance_meters: null,
                gross_dynamic_duration_minutes: null,
                effective_stay_duration: null,
                actual_departure_time: stop.waypoint_departure_time ?? null,
                is_segment_distance_planned: false
            }));
        }
        const isPreTripStaticDistance = ['scheduled', 'dispatched'].includes(tripData?.status);
        const staticEstimatedRows = Array.isArray(etaData?.estimated)
            ? etaData.estimated : (etaData?.estimated?.waypoints || []);
        const isPreTripWithoutMetrics = isPreTripStaticDistance && staticEstimatedRows.length === 0;
        // Do not let a retained live payload change the planned rider-card
        // distance before departure. It becomes authoritative only once the
        // trip has entered its live lifecycle.
        const liveEtaPayload = tripData?.status === 'in_progress'
            ? (etaData?.live || etaData?.websocket || null)
            : null;
        const isWebsocketData = Boolean(liveEtaPayload);
        const isCompletedActualDisplay = displayData?.type?.includes('completed')
            && displayData?.source === 'actual_trip_data';
        const completedDisplayWaypoints = isCompletedActualDisplay
            ? sortWaypointsByDisplayOrder(displayData.waypoints || [])
            : [];
        const completedActualWaypoints = isCompletedActualDisplay
            ? sortWaypointsByDisplayOrder(etaData?.actual || [])
            : [];
        const completedEstimatedWaypoints = isCompletedActualDisplay
            ? sortWaypointsByDisplayOrder(getCompletedEstimatedWaypoints(etaData?.estimated))
            : [];
        const completedActualWaypointMap = new Map(
            completedActualWaypoints.map((waypoint) => [getWaypointKey(waypoint.waypoint_id), waypoint])
        );
        const completedEstimatedWaypointMap = new Map(
            completedEstimatedWaypoints.map((waypoint) => [getWaypointKey(waypoint.waypoint_id), waypoint])
        );
        const poisonedCompletedAnchorKeys = isCompletedActualDisplay
            ? buildPoisonedCompletedAnchorKeySet(
                allStops,
                completedActualWaypointMap,
                completedEstimatedWaypointMap
            )
            : new Set();

        console.log(`All stops found: ${allStops.length}`);

        // Calculate dynamic distances and durations based on previous visible stop
        let cumulativeCompletedDistance = 0;
        let completedDistanceChainIntact = true;

        const stopsWithDynamicDistances = allStops.map((stop, index) => {
            let dynamicDistance = 0;
            let dynamicDuration = 0;
            let grossDynamicDistance = 0;
            let grossDynamicDuration = 0;
            let adjustedStayDuration = 0;
            let actualDepartureTime = null;
            // True when this segment's distance came from planned estimates rather than real GPS
            let isSegmentDistancePlanned = false;
            const isCompletedSkippedStop = isCompletedDisplaySkippedStop(allStops, index);
            let completedSegmentDistanceKnown = !isCompletedActualDisplay;
            let completedSegmentDurationKnown = !isCompletedActualDisplay;
            let completedGrossDurationKnown = !isCompletedActualDisplay;

            if (index === 0) {
                // For websocket data where start location is filtered, use the original distance
                if (displayData?.type === 'websocket_realtime' && tripData?.status === 'in_progress') {
                    dynamicDistance = parseNullableNonNegativeMetric(stop.total_distance_meters);
                    dynamicDuration = parseNullableNonNegativeMetric(stop.trip_duration_minutes);
                    grossDynamicDistance = dynamicDistance;
                    grossDynamicDuration = dynamicDuration;
                } else {
                    // First stop has no incoming segment
                    dynamicDistance = 0;
                    dynamicDuration = 0;
                    grossDynamicDistance = 0;
                    grossDynamicDuration = 0;
                }
                if (isCompletedActualDisplay) {
                    completedSegmentDistanceKnown = true;
                    completedSegmentDurationKnown = true;
                    completedGrossDurationKnown = true;
                }
                // First stop can have stay duration
                adjustedStayDuration = stop.stay_duration_minutes || 0;

                // Calculate actual departure time for completed trips (Arrival + Stay)
                if (isCompletedActualDisplay) {
                    const firstActualStop = completedActualWaypointMap.get(getWaypointKey(stop.waypoint_id));
                    if (firstActualStop && firstActualStop.actual_arrival_time) {
                        const arrivalTime = new Date(firstActualStop.actual_arrival_time);
                        const stayDurationMs = (adjustedStayDuration || 0) * 60 * 1000;

                        // Departure time = arrival + stay duration
                        actualDepartureTime = new Date(arrivalTime.getTime() + stayDurationMs);

                        console.log(`[ETADisplay] First stop ${stop.waypoint_name}:`, {
                            actual_arrival: firstActualStop.actual_arrival_time,
                            stay_duration_minutes: adjustedStayDuration,
                            actual_departure: actualDepartureTime.toISOString()
                        });
                    }
                }
            } else {
                const prevVisibleStop = allStops[index - 1];
                const isLastStop = index === allStops.length - 1;

                // Set stay duration (zero for last stop)
                adjustedStayDuration = isLastStop ? 0 : (stop.stay_duration_minutes || 0);

                // For completed trips with actual data, use actual_arrival_time
                if (isCompletedActualDisplay) {
                    const currentActualStop = completedActualWaypointMap.get(getWaypointKey(stop.waypoint_id));

                    if (currentActualStop) {
                        const previousCompletedAnchorStop = findPreviousCompletedAnchorStop(allStops, index);
                        const displaySegmentWaypoints = getWaypointsInCompletedSegment(
                            completedDisplayWaypoints,
                            previousCompletedAnchorStop,
                            stop
                        );
                        const actualRawSegmentWaypoints = getWaypointsInCompletedSegment(
                            completedActualWaypoints,
                            previousCompletedAnchorStop,
                            stop
                        );
                        const estimatedSegmentWaypoints = getWaypointsInCompletedSegment(
                            completedEstimatedWaypoints,
                            previousCompletedAnchorStop,
                            stop
                        );
                        const canUseEstimatedFallback = canUseEstimatedCompletedSegmentFallback(
                            displaySegmentWaypoints,
                            estimatedSegmentWaypoints
                        );
                        const previousActualStop = previousCompletedAnchorStop
                            ? completedActualWaypointMap.get(getWaypointKey(previousCompletedAnchorStop.waypoint_id))
                            : null;
                        const canUseActualSegmentDistance = Boolean(previousActualStop?.actual_arrival_time)
                            && Boolean(currentActualStop.actual_arrival_time)
                            && doesCompletedSegmentTopologyMatch(
                                actualRawSegmentWaypoints,
                                displaySegmentWaypoints
                            );

                        if (!isCompletedSkippedStop && previousCompletedAnchorStop) {
                            let segmentDistance = 0;
                            let hasDistanceSource = false;
                            // Track whether any waypoint in this segment used the estimated fallback
                            let usedEstimatedFallback = false;

                            displaySegmentWaypoints.forEach((waypoint) => {
                                const waypointKey = getWaypointKey(waypoint.waypoint_id);
                                const isPoisonedCompletedAnchor = poisonedCompletedAnchorKeys.has(waypointKey);
                                const isSkippedSegmentWaypoint = Boolean(waypoint?.is_skipped);
                                const actualWaypoint = completedActualWaypointMap.get(waypointKey);
                                const actualDistance = parseFloat(actualWaypoint?.total_distance_meters) || 0;
                                const estimatedWaypoint = completedEstimatedWaypointMap.get(waypointKey);
                                const estimatedDistance = parseFloat(estimatedWaypoint?.total_distance_meters) || 0;
                                const previousDisplayWaypoint = findPreviousWaypointInOrderedList(
                                    completedDisplayWaypoints,
                                    waypoint
                                );
                                const previousDisplayActualWaypoint = previousDisplayWaypoint
                                    ? completedActualWaypointMap.get(getWaypointKey(previousDisplayWaypoint.waypoint_id))
                                    : null;
                                const actualStepWaypoints = previousDisplayWaypoint
                                    ? getWaypointsInCompletedSegment(
                                        completedActualWaypoints,
                                        previousDisplayWaypoint,
                                        waypoint
                                    )
                                    : [];
                                const displayStepWaypoints = previousDisplayWaypoint
                                    ? getWaypointsInCompletedSegment(
                                        completedDisplayWaypoints,
                                        previousDisplayWaypoint,
                                        waypoint
                                    )
                                    : [];
                                const canUseActualDistanceForWaypoint = Boolean(previousDisplayActualWaypoint?.actual_arrival_time)
                                    && Boolean(actualWaypoint?.actual_arrival_time)
                                    && doesCompletedSegmentTopologyMatch(
                                        actualStepWaypoints,
                                        displayStepWaypoints
                                    );
                                const canBridgeSkippedIntermediateRows = waypointKey === getWaypointKey(stop.waypoint_id)
                                    && !isSkippedSegmentWaypoint
                                    && Boolean(actualWaypoint?.actual_arrival_time)
                                    && hasOnlySkippedCompletedIntermediates(displaySegmentWaypoints, waypoint);

                                if (
                                    canUseActualSegmentDistance
                                    && (canUseActualDistanceForWaypoint || canBridgeSkippedIntermediateRows)
                                    && !isPoisonedCompletedAnchor
                                ) {
                                    const isCompletedDistanceFallback = Boolean(actualWaypoint?.is_completed_distance_fallback);
                                    const actualZeroLooksIntentional = actualDistance === 0
                                        && (!estimatedWaypoint || estimatedDistance === 0);
                                    if (actualDistance > 0 || actualZeroLooksIntentional) {
                                        segmentDistance += actualDistance;
                                        hasDistanceSource = true;
                                        if (isCompletedDistanceFallback) {
                                            usedEstimatedFallback = true;
                                        }
                                        return;
                                    }
                                }

                                // Skipped rows are historical display evidence, not traveled distance
                                // anchors. Do not roll their planned distance into the next real stop.
                                if (!canUseEstimatedFallback || isSkippedSegmentWaypoint) {
                                    return;
                                }

                                hasDistanceSource = true;
                                if (estimatedDistance > 0) {
                                    console.log(`[ETADisplay] Using estimated segment distance fallback for waypoint ${waypoint.waypoint_name}: ${estimatedDistance}m (actual was ${actualDistance}${isPoisonedCompletedAnchor ? ', poisoned-anchor override' : ''})`);
                                    segmentDistance += estimatedDistance;
                                    usedEstimatedFallback = true;
                                }
                            });

                            completedSegmentDistanceKnown = hasDistanceSource;
                            dynamicDistance = segmentDistance;
                            isSegmentDistancePlanned = usedEstimatedFallback;

                            if (previousActualStop?.actual_arrival_time && currentActualStop.actual_arrival_time) {
                                const prevArrivalTime = new Date(previousActualStop.actual_arrival_time);
                                const prevStayDuration = (parseFloat(previousActualStop.stay_duration_seconds) || 0) / 60;
                                const prevDepartureTime = new Date(prevArrivalTime.getTime() + prevStayDuration * 60 * 1000);
                                const currentArrivalTime = new Date(currentActualStop.actual_arrival_time);

                                dynamicDuration = Math.max(0, (currentArrivalTime - prevDepartureTime) / (60 * 1000));
                                completedSegmentDurationKnown = true;
                            } else {
                                dynamicDuration = null;
                                completedSegmentDurationKnown = false;
                            }
                        }

                        if (!isCompletedSkippedStop) {
                            if (completedSegmentDistanceKnown && completedDistanceChainIntact) {
                                cumulativeCompletedDistance += dynamicDistance || 0;
                            } else if (index > 0) {
                                completedDistanceChainIntact = false;
                                cumulativeCompletedDistance = 0;
                            }
                        }

                        grossDynamicDistance = isCompletedSkippedStop
                            ? 0
                            : (completedDistanceChainIntact ? cumulativeCompletedDistance : 0);

                        const firstWaypoint = completedDisplayWaypoints[0];
                        if (firstWaypoint?.actual_arrival_time && currentActualStop.actual_arrival_time) {
                            const startTime = new Date(firstWaypoint.actual_arrival_time);
                            const currentTime = new Date(currentActualStop.actual_arrival_time);
                            grossDynamicDuration = Math.max(0, (currentTime - startTime) / (60 * 1000));
                            completedGrossDurationKnown = true;
                            console.log(`[ETADisplay] Gross duration calculation for ${stop.waypoint_name}: ${firstWaypoint.actual_arrival_time} to ${currentActualStop.actual_arrival_time} = ${grossDynamicDuration} minutes`);
                        } else {
                            grossDynamicDuration = null;
                            completedGrossDurationKnown = false;
                        }

                        // Calculate actual departure time for completed trips (Arrival + Stay)
                        if (currentActualStop.actual_arrival_time) {
                            const arrivalTime = new Date(currentActualStop.actual_arrival_time);
                            const stayDurationMs = (adjustedStayDuration || 0) * 60 * 1000;
                            actualDepartureTime = new Date(arrivalTime.getTime() + stayDurationMs);
                        }

                        console.log(`[ETADisplay] Stop ${stop.waypoint_name} (using backend data):`, {
                            actual_arrival: currentActualStop.actual_arrival_time,
                            actual_departure: actualDepartureTime?.toISOString(),
                            segmentDurationMinutes: dynamicDuration,
                            segmentDistanceMeters: dynamicDistance,
                            grossDurationMinutes: grossDynamicDuration,
                            grossDistanceMeters: grossDynamicDistance
                        });
                    }
                } else {
                    // Use estimated data calculation (logic for non-completed trips)
                    const allEstimatedWaypoints = liveEtaPayload ?
                        sortWaypointsByDisplayOrder(liveEtaPayload.waypoints || [])
                        : isPreTripWithoutMetrics ? []
                        : etaData?.estimated ? sortWaypointsByDisplayOrder(etaData.estimated)
                        : [];

                    const currentSequence = getWaypointOrderValue(stop);
                    const prevSequence = getWaypointOrderValue(prevVisibleStop);

                    // Sum distances for all waypoints between previous visible and current (inclusive of current)
                    const waypointsInRange = allEstimatedWaypoints.filter(wp =>
                        getWaypointOrderValue(wp) > prevSequence && getWaypointOrderValue(wp) <= currentSequence
                    );

                    console.log(`[ETADisplay] Waypoints in range: ${waypointsInRange.length}`, waypointsInRange);

                    // Use the correct distance field based on data source
                    dynamicDistance = isPreTripStaticDistance
                        ? sumRequiredStaticDistanceMeters(waypointsInRange)
                        : sumRequiredMetrics(waypointsInRange, isWebsocketData
                            ? 'distance_from_previous_waypoint' : 'total_distance_meters');

                    // Use the correct duration field based on data source
                    dynamicDuration = sumRequiredMetrics(waypointsInRange, isWebsocketData
                        ? 'predicted_trip_duration_minutes' : 'total_duration_minutes');

                    // For websocket data, use the backend-provided distance_meters_from_current
                    // This represents the actual remaining distance from current location
                    if (isWebsocketData && stop.distance_meters_from_current !== undefined) {
                        grossDynamicDistance = stop.distance_meters_from_current;
                        grossDynamicDuration = parseNullableNonNegativeMetric(stop.eta_minutes_from_now);
                    } else {
                        // Fallback: calculate cumulative distance from remaining waypoints only
                        // This prevents including distances from passed waypoints
                        const remainingWaypoints = allEstimatedWaypoints.filter(wp =>
                            getWaypointOrderValue(wp) > prevSequence && getWaypointOrderValue(wp) <= currentSequence
                        );

                        grossDynamicDistance = isPreTripStaticDistance
                            ? sumRequiredStaticDistanceMeters(remainingWaypoints)
                            : sumRequiredMetrics(remainingWaypoints, isWebsocketData
                                ? 'distance_from_previous_waypoint' : 'total_distance_meters');

                        grossDynamicDuration = sumRequiredMetrics(remainingWaypoints, isWebsocketData
                            ? 'predicted_trip_duration_minutes' : 'total_duration_minutes');
                    }
                }
            }

            if (isPreTripWithoutMetrics && !isStartLikeWaypoint(stop)) {
                // An empty metric source proves neither a zero-length leg nor
                // a zero total. This also covers a first visible non-origin row;
                // independently supplied cumulative trip fields survive below.
                dynamicDistance = null;
                dynamicDuration = null;
                grossDynamicDistance = null;
                grossDynamicDuration = null;
            }

            // Calculate stay duration only when there are actual pickups/dropoffs
            const hasActivity = (stop.pickup_riders > 0 || stop.dropoff_riders > 0);
            const effectiveStayDuration = hasActivity ? adjustedStayDuration : 0;

            // Keep backend/WebSocket eta_minutes_from_now as source of truth.
            const rawEtaMinutes = parseNullableNonNegativeMetric(stop.eta_minutes_from_now);
            const etaMinutesFromNow = rawEtaMinutes !== null
                ? Math.max(0, Math.round(rawEtaMinutes))
                : null;

            return {
                ...stop,
                is_segment_distance_planned: isSegmentDistancePlanned,
                dynamic_distance_meters: normalizeNonNegativeNumber(dynamicDistance),
                dynamic_duration_minutes: isCompletedActualDisplay && !completedSegmentDurationKnown
                    ? null
                    : normalizeNonNegativeNumber(dynamicDuration),
                gross_dynamic_distance_meters: isPreTripStaticDistance
                    ? normalizeNonNegativeNumber(grossDynamicDistance)
                    : Math.max(0, grossDynamicDistance),
                gross_dynamic_duration_minutes: isCompletedActualDisplay && !completedGrossDurationKnown
                    ? null
                    : normalizeNonNegativeNumber(grossDynamicDuration),
                effective_stay_duration: effectiveStayDuration,
                stay_duration_minutes: adjustedStayDuration, // Override with adjusted stay duration
                actual_departure_time: actualDepartureTime,
                eta_minutes_from_now: etaMinutesFromNow,
                distance_source: stop.distance_source ?? null,
                segment_distance_source: stop.segment_distance_source ?? null,
                distance_authority: stop.distance_authority ?? null,
                is_completed_distance_fallback: Boolean(stop.is_completed_distance_fallback),
                eta_calculation_source: stop.eta_calculation_source ?? null
            };
        });

        return sortWaypointsByDisplayOrder(stopsWithDynamicDistances);
    }, [displayData, isDriver, pickupStopId, dropoffStopId, tripData?.status, showWaypoints, etaData, t]);
};

export default useRelevantStops;
